/**
 * OwedList — V98-3-3 / 3-4. ONE urgency-sorted list (the server's order),
 * each item once, a single hat tag per row (R4: no numbered badges, no
 * "as <hat>" subtitles). Hat filter chips only when > 8 rows and > 1 tag.
 *
 * Each row: tag · title (program-neutral item link with a return crumb) ·
 * the date chip (red only here, its highest tier) · an SoD state when the
 * viewer prepared it · ONE action. The action follows the server's action
 * contract (V98-3 review U1, S10): a plain completion acts inline ("Mark
 * done"), an owning record opens directly (e.g. "Add the coverage
 * document"), everything else opens the item. Its accessible name starts
 * with its visible label (WCAG 2.5.3) and appends the item title.
 *
 * One-approver org (U8): no hat tags (one person holds every hat) and no
 * per-row "Add advisor" — the rows share ONE state ("Needs a second
 * person") and the solo card owns the fix.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@aigovops/design-system';
import type { OwedRow, WaitingRow } from '@aigovops/core';
import { completeWorkItem } from '../../lib/coverage-api';
import { actionName, dueChip, firstName, fmtDay, hatTag, itemHref, nameOf, rowAct, sodNote, type Names } from '../../lib/today-model';
import { Chip, domId, errorText } from './shared';

export interface OwedListProps {
  rows: readonly OwedRow[];
  names: Names;
  prefix: string;
  slug: string;
  todayDay: string;
  programNames: ReadonlyMap<string, string>;
  solo: boolean;
  showHatFilter: boolean;
  emptyAction?: React.ReactNode;
  label: string;
  /** After an inline action: announce + move focus to the new state. */
  onDone: (msg: string, focusId: string) => void;
}

export function OwedList({ rows, names, prefix, slug, todayDay, programNames, solo, showHatFilter, emptyAction, label, onDone }: OwedListProps) {
  const [filter, setFilter] = useState<string>('all');
  const tags = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(hatTag(r, names), (m.get(hatTag(r, names)) ?? 0) + 1);
    return [...m];
  }, [rows, names]);
  const active = showHatFilter && !solo && filter !== 'all' && tags.some(([t]) => t === filter) ? filter : 'all';
  const shown = active === 'all' ? rows : rows.filter((r) => hatTag(r, names) === active);

  if (rows.length === 0) {
    return (
      <ul className="gp-owed-list" aria-label={label}>
        <li className="gp-ow" id="owed-empty" tabIndex={-1}><span className="gp-ow-main"><span className="gp-ow-t">Nothing owed by you</span></span>{emptyAction ? <span className="gp-ow-act">{emptyAction}</span> : null}</li>
      </ul>
    );
  }
  return (
    <>
      {showHatFilter && !solo ? (
        // A segmented filter: ONE selected state (aria-pressed + a filled cell), never a second primary (U6).
        <div className="gp-segment" role="group" aria-label="Filter by hat">
          <button type="button" className="gp-seg" aria-pressed={active === 'all'} onClick={() => setFilter('all')}>{`All · ${rows.length}`}</button>
          {tags.map(([t, n]) => (
            <button key={t} type="button" className="gp-seg" aria-pressed={active === t} onClick={() => setFilter(t)}>{`${t} · ${n}`}</button>
          ))}
        </div>
      ) : null}
      <ol className="gp-owed-list" aria-label={label}>
        {shown.map((r, i) => (
          <OwedItem key={r.key} row={r} names={names} prefix={prefix} slug={slug} todayDay={todayDay} programNames={programNames} solo={solo}
            nextFocus={shown[i + 1] ? domId('ow', shown[i + 1]!.key) : shown[i - 1] ? domId('ow', shown[i - 1]!.key) : 'owed-h'} onDone={onDone} />
        ))}
      </ol>
    </>
  );
}

function OwedItem({ row, names, prefix, slug, todayDay, programNames, solo, nextFocus, onDone }: {
  row: OwedRow; names: Names; prefix: string; slug: string; todayDay: string; programNames: ReadonlyMap<string, string>; solo: boolean;
  nextFocus: string; onDone: (msg: string, focusId: string) => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // V98-4 review: a fact question shows when Legal asked (it has no due date), never "No date".
  const chip = row.action === 'answer_fact' && row.asked_at ? { text: `Asked ${fmtDay(row.asked_at)}`, tone: 'neutral' as const } : dueChip(row, todayDay);
  const note = sodNote(row, solo);
  const href = itemHref(prefix, row.ref);
  // R3-U5: a one-approver org's "needs a second person" rows carry no per-row "Add advisor" — the solo card
  // owns that fix — so each just opens its item.
  const act = solo && note ? { kind: 'link' as const, to: href, label: 'Open' } : rowAct(row, prefix);
  const programs = row.program_ids.map((p) => programNames.get(p)).filter(Boolean) as string[];
  const id = domId('ow', row.key);
  const complete = async (workItemId: string) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await completeWorkItem(slug, workItemId);
      await qc.invalidateQueries({ queryKey: ['today', slug] });
      onDone(`Done · ${row.title}`, nextFocus);
    } catch (e) {
      setError(errorText(e, 'It could not be marked done.'));
      onDone('It could not be marked done', id);
    } finally { setBusy(false); }
  };
  return (
    <li className="gp-ow" id={id} tabIndex={-1} data-tag={solo ? undefined : hatTag(row, names)}>
      <span className="gp-ow-main">
        <span className="gp-ow-line">
          {solo ? null : <span className="gp-htag">{hatTag(row, names)}</span>}
          <Link className="gp-ow-t" to={href}>{row.title}</Link>
        </span>
        <span className="gp-ow-meta">
          {/* Blocked rows show their SoD state instead of an amber "blocked" chip (one signal, not two). */}
          {note && chip.tone === 'amber' ? null : <Chip text={chip.text} tone={chip.tone} />}
          {note ? <Chip text={note} tone="amber" /> : null}
          {row.quantity > 1 ? <span>{`${row.quantity} rows`}</span> : null}
          {programs.length ? <span>{programs.join(' · ')}</span> : null}
        </span>
        {error ? <span className="gp-field-err" role="alert">{error}</span> : null}
      </span>
      <span className="gp-ow-act">
        {act.kind === 'complete'
          ? <Button compact aria-disabled={busy || undefined} aria-label={actionName(act.label, row.title)} onClick={() => void complete(act.workItemId)}>{busy ? 'Saving…' : act.label}</Button>
          : <Link className="gp-linkbtn gp-linkbtn--compact" to={act.to} aria-label={actionName(act.label, row.title)}>{act.label}</Link>}
      </span>
    </li>
  );
}

/** Waiting on others — the viewer's, never counted as owed. "As requester" is not "author" (U14). */
export function WaitingList({ rows, names, prefix, todayDay }: { rows: readonly WaitingRow[]; names: Names; prefix: string; todayDay: string }) {
  const AS: Record<string, string> = {
    preparer: 'You wrote this', requester: 'You requested this', delegator: 'Delegated by you', asker: 'You asked', self_approver: 'Check of your approval',
    access_reviewer: 'You reviewed this',
  };
  return (
    <ul className="gp-owed-list gp-owed-list--flat" aria-label="Waiting on others">
      {rows.map((r) => {
        const who = nameOf(names, r.waiting_on_user_id, 'Nobody yet');
        const chip = dueChip(r, todayDay);
        return (
          <li key={r.key} className="gp-ow gp-ow--wait" id={domId('wait', r.key)}>
            <span className="gp-ow-main">
              <span className="gp-ow-line"><Link className="gp-ow-t" to={itemHref(prefix, r.ref)}>{r.title}</Link></span>
              <span className="gp-ow-meta">
                {/* R3-U22: flat rows — plain meta text, no chip boxes. */}
                <span>{`${AS[r.as] ?? 'Yours'} · with ${who}`}</span>
                <span>{chip.text}</span>
              </span>
            </span>
            <span className="gp-ow-act">
              <Link className="gp-linkbtn gp-linkbtn--compact" to={itemHref(prefix, r.ref)} aria-label={`Open · ${r.title} · with ${firstName(who)}`}>Open</Link>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
